import CryptoJS from 'crypto-js';
import { logout, getCurrentUserDetail } from '@/services/ant-design-pro/api';
import { normalizeInstitutionId } from './organizationUtils';

// Token 存储的 key
const TOKEN_KEY = 'jianyin_token';
const USER_INFO_KEY = 'jianyin_user_info';

/**
 * MD5 加密
 * @param text 要加密的文本
 * @returns 加密后的字符串
 */
export const md5 = (text: string): string => {
  return CryptoJS.MD5(text).toString();
};

/**
 * 保存加密后的 token 到 localStorage
 * @param token 原始 token
 * @param userInfo 用户信息
 */
export const saveToken = (token: string, userInfo?: any): void => {
  try {
    // 使用 MD5 加密 token
    const encryptedToken = md5(token);

    // 保存加密后的 token
    localStorage.setItem(TOKEN_KEY, encryptedToken);

    // 保存用户信息
    if (userInfo) {
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
    }
  } catch (error) {
    console.error('保存 token 失败:', error);
  }
};

/**
 * 从 localStorage 获取加密后的 token
 * @returns 加密后的 token 或 null
 */
export const getToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('获取 token 失败:', error);
    return null;
  }
};

/**
 * 从 localStorage 获取用户信息
 * @returns 用户信息对象或 null
 */
export const getUserInfo = (): any => {
  try {
    const userInfoStr = localStorage.getItem(USER_INFO_KEY);
    return userInfoStr ? JSON.parse(userInfoStr) : null;
  } catch (error) {
    console.error('获取用户信息失败:', error);
    return null;
  }
};

/**
 * 清除 token 和用户信息
 */
export const clearToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
  } catch (error) {
    console.error('清除 token 失败:', error);
  }
};

/**
 * 强制登出：清除 token 并跳转到登录页面
 * @param useReplace 是否使用 replace 方式跳转，默认为 true
 */
export const forceLogout = (useReplace: boolean = true): void => {
  try {
    // 清除本地存储
    clearToken();

    // 跳转到登录页面
    if (useReplace) {
      // 使用 replace 方式跳转，避免用户可以通过后退按钮返回到之前的页面
      window.location.replace('/user/login');
    } else {
      window.location.href = '/user/login';
    }

  } catch (error) {
    console.error('强制登出失败:', error);
  }
};

/**
 * 退出登录：调用退出接口并清除本地数据
 * @param useReplace 是否使用 replace 方式跳转，默认为 true
 */
export const handleLogout = async (useReplace: boolean = true): Promise<void> => {
  try {
    // 调用退出接口

    try {
      await logout();
    } catch (error) {
      console.warn('退出接口调用失败，但继续清除本地数据:', error);
    }

    // 无论接口是否成功，都清除本地数据
    clearToken();

    // 跳转到登录页面
    if (useReplace) {
      // 使用 replace 方式跳转，避免用户可以通过后退按钮返回到之前的页面
      window.location.replace('/user/login');
    } else {
      window.location.href = '/user/login';
    }

  } catch (error) {
    console.error('退出登录失败:', error);
    // 即使出错，也强制清除本地数据
    clearToken();
    if (useReplace) {
      window.location.replace('/user/login');
    } else {
      window.location.href = '/user/login';
    }
  }
};

/**
 * 检查是否已登录
 * @returns 是否已登录
 */
export const isLoggedIn = (): boolean => {
  return !!getToken();
};

/**
 * 获取请求头中的 Authorization
 * @returns Authorization header 值
 */
export const getAuthHeader = (): string => {
  const token = getToken();
  return token ? `Bearer ${token}` : '';
};

/**
 * 获取当前登录用户详情
 */
const withNormalizedInstitutionId = (userInfo: any) => {
  if (!userInfo) {
    return userInfo;
  }
  const institutionId = normalizeInstitutionId(userInfo.currentInstitutionId);
  if (!institutionId) {
    return userInfo;
  }
  return {
    ...userInfo,
    currentInstitutionId: institutionId,
  };
};

export const getCurrentUserInfo = async (): Promise<any> => {
  try {
    // 首先尝试从本地存储获取
    const localUserInfo = getUserInfo();

    // 检查本地存储的用户信息是否包含机构信息（currentInstitutionId）
    // 如果没有，则需要从服务器获取完整信息
    if (localUserInfo && localUserInfo.currentInstitutionId !== undefined && localUserInfo.currentInstitutionId !== null) {
      return withNormalizedInstitutionId(localUserInfo);
    }

    // 如果本地没有或缺少机构信息，则从服务器获取
    const response = await getCurrentUserDetail();

    if (response?.success && response?.result) {
      const merged = withNormalizedInstitutionId({
        ...(localUserInfo || {}),
        ...response.result,
        // /own 未返回该字段时保留本地（例如仅 auth 下发过）
        needChangePassword: response.result.needChangePassword ?? localUserInfo?.needChangePassword,
        initialPasswordUnchanged:
          response.result.initialPasswordUnchanged ?? localUserInfo?.initialPasswordUnchanged,
      });
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(merged));
      return merged;
    } else {
      // 如果接口调用失败，返回本地存储的信息（即使没有机构信息）
      return withNormalizedInstitutionId(localUserInfo || null);
    }
  } catch (error) {
    // 如果出错，返回本地存储的信息（即使没有机构信息）
    const localUserInfo = getUserInfo();
    return withNormalizedInstitutionId(localUserInfo || null);
  }
};
